/**
 * Smart Token Value Detector
 * Auto-detects if a token is high-value or low-value based on:
 * 1. Token price (USD)
 * 2. User balance amount
 * 3. Known high-value tokens (fallback)
 */

// Known high-value tokens (stablecoins, major coins)
const KNOWN_HIGH_VALUE_TOKENS = [
  'BNB', 'WBNB', 'ETH', 'WETH', 'BTCB', 'BTC',
  'USDT', 'USDC', 'BUSD', 'DAI', 'TUSD',
  'CAKE', 'ADA', 'DOT', 'MATIC'
];

/**
 * Determines if a token should display decimal places
 * @param {Object} token - Token object with symbol
 * @param {Number} priceUSD - Token price in USD (optional)
 * @param {Number} balance - User balance amount (optional)
 * @returns {Boolean} - true if high-value (show decimals), false if low-value (hide decimals)
 */
export const isHighValueToken = (token, priceUSD = null, balance = null) => {
  if (!token) return false;

  const symbol = token.symbol?.toUpperCase() || '';

  // 1. Check known high-value tokens first (fastest)
  if (KNOWN_HIGH_VALUE_TOKENS.includes(symbol)) {
    return true;
  }

  // 2. If we have price data, use it (most reliable)
  if (priceUSD !== null && priceUSD !== undefined && priceUSD > 0) {
    // If token price > $0.01, it's likely high-value
    // Users typically transact in small amounts (0.1, 0.5, etc)
    if (priceUSD > 0.01) {
      return true;
    }
    
    // If price is very low (< $0.0001), definitely low-value
    if (priceUSD < 0.0001) {
      return false;
    }
  }

  // 3. If we have balance data, use it as indicator
  if (balance !== null && balance !== undefined && balance > 0) {
    // If user has < 100 tokens, likely high-value
    // (people don't usually have 0.5 BNB, but might have 500,000 LRST)
    if (balance < 100) {
      return true;
    }
    
    // If user has > 10,000 tokens, likely low-value
    if (balance > 10000) {
      return false;
    }
  }

  // 4. Default behavior: assume low-value (hide decimals)
  // This is safer for most community/meme tokens
  return false;
};

/**
 * Smart number formatting based on token value type
 * @param {Number} amount - Amount to format
 * @param {Object} token - Token object
 * @param {Number} priceUSD - Token price in USD (optional)
 * @param {Number} balance - User balance (optional)
 * @param {Number} maxDecimals - Maximum decimals for high-value tokens (default: 6)
 * @returns {String} - Formatted amount
 */
export const formatTokenAmount = (amount, token, priceUSD = null, balance = null, maxDecimals = 6) => {
  if (!amount || isNaN(amount)) return '0';
  
  const num = Number(amount);
  if (num === 0) return '0';

  const isHighValue = isHighValueToken(token, priceUSD, balance);

  if (isHighValue) {
    // High-value token: show decimals
    if (num < 0.000001) {
      return num.toExponential(2);
    }
    if (num < 1) {
      return num.toFixed(maxDecimals).replace(/\.?0+$/, '');
    }
    if (num < 1000) {
      return num.toFixed(Math.min(6, maxDecimals)).replace(/\.?0+$/, '');
    }
    // Large amounts still get some decimals
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  } else {
    // Low-value token: hide decimals
    if (num < 1) {
      // Very small amounts still need decimals (e.g., 0.5)
      return num.toFixed(2).replace(/\.?0+$/, '');
    }
    // Remove all decimals for amounts >= 1
    return Math.floor(num).toLocaleString('en-US');
  }
};

/**
 * Format balance display
 * @param {Number} balance - Balance amount
 * @param {Object} token - Token object
 * @param {Number} priceUSD - Token price in USD (optional)
 * @returns {String} - Formatted balance
 */
export const formatBalance = (balance, token, priceUSD = null) => {
  return formatTokenAmount(balance, token, priceUSD, balance, 6);
};

/**
 * Check if amount input should allow decimals
 * @param {Object} token - Token object
 * @param {Number} priceUSD - Token price in USD (optional)
 * @returns {Boolean} - true if decimals allowed
 */
export const allowDecimals = (token, priceUSD = null) => {
  return isHighValueToken(token, priceUSD, null);
};
